'use client'
import { useEffect, useImperativeHandle, forwardRef } from 'react'
import LobbyProfileCard from '../public-profile-card/lobbyProfileCard'
import { TypesenseData } from '@/lib/typesenseFunction'
import FlowbiteIconComponent from '../flowbite-icon/flowbiteIcon'
import { useEmblaCarouselResponsive } from '@/utils/react-hooks/useEmblaCarousel'
import Autoplay from 'embla-carousel-autoplay'

interface IProps {
  profiles: TypesenseData[]
  onSelectedIndexChange?: (index: number) => void
}

export interface FeaturedProfilesRef {
  stopAutoplay: () => void
}

export const FeaturedProfiles = forwardRef<FeaturedProfilesRef, IProps>(
  ({ profiles, onSelectedIndexChange }, ref) => {
    const { emblaRef, scrollPrev, scrollNext, selectedIndex, emblaApi } =
      useEmblaCarouselResponsive({
        options: { loop: true, align: 'center', watchDrag: true },
        plugins: [
          Autoplay({
            playOnInit: true,
            delay: 3000,
            stopOnInteraction: true,
          }),
        ],
      })

    useImperativeHandle(
      ref,
      () => ({
        stopAutoplay: () => {
          emblaApi?.plugins()?.autoplay?.stop()
        },
      }),
      [emblaApi],
    )

    useEffect(() => {
      onSelectedIndexChange?.(selectedIndex)
    }, [selectedIndex, onSelectedIndexChange])

  return (
    <div className="flex flex-col justify-center items-center relative w-full">
      <div className="flex w-full relative justify-center items-center h-full pb-[50px]">
        <div className="absolute inset-0 bg-black h-full w-full flex items-center justify-center" />

          <div className="absolute inset-0 h-full w-full flex items-center justify-center">
            <div className="mr-[120px] z-40 transition-opacity duration-300 ease-in-out opacity-100">
              <button
                onClick={() => {
                  scrollPrev()
                  emblaApi?.plugins()?.autoplay?.stop()
                }}
                className="h-[35px] w-[35px] flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed"
                aria-label="Scroll left"
              >
                <FlowbiteIconComponent
                  iconType="AngleDown"
                  className="rotate-90 text-white !ml-0"
                  iconClassName="w-6 h-6"
                />
              </button>
            </div>

            <div className="ml-[120px] z-50  duration-300 ease-in-out opacity-100">
              <button
                onClick={() => {
                  scrollNext()
                  emblaApi?.plugins()?.autoplay?.stop()
                }}
                className="h-[35px] w-[35px] flex items-center justify-center disabled:opacity-40"
                aria-label="Scroll right"
              >
                <FlowbiteIconComponent
                  iconType="AngleDown"
                  className="-rotate-90 text-white !ml-0"
                  iconClassName="w-6 h-6"
                />
              </button>
            </div>
          </div>

          <div className="w-full overflow-hidden relative" ref={emblaRef}>
            <div className="flex relative py-5">
              {profiles.map((profile, index: number) => {
                const isCenterCard = index === selectedIndex
                return (
                  <div
                    key={`${profile.slug}-${index}`}
                    className={`
                  basis-[220px] shrink-0
                  flex justify-center items-center flex-col
                  transition-opacity duration-500 ease-in-out mx-2
                  ${isCenterCard ? '' : 'opacity-20 pointer-events-none'}
                `}
                  >
                    <LobbyProfileCard
                      profileData={profile}
                      isHomepage={true}
                      showSeeProfileBtn={isCenterCard}
                    />
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </div>
    )
  },
)

FeaturedProfiles.displayName = 'FeaturedProfiles'

export default FeaturedProfiles
